import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';
import { adminAPI } from '../../services/api';

interface Order {
  id: number;
  uuid: string;
  customer_name: string;
  dispatcher_name?: string;
  dispatcher_id?: number;
  pickup_location: string;
  dropoff_location: string;
  status: string;
  final_price: number;
  created_at: string;
  completed_at?: string;
  vehicle_type: string;
  package_description?: string;
  package_type?: string;
  pickup_photo_url?: string;
  delivery_photo_url?: string;
  admin_approval_status?: string;
  admin_notes?: string;
  stops: any[];
}

interface Props {
  order: Order;
  onClose: () => void;
  onUpdate: () => void;
}

const DeliveryReviewModal: React.FC<Props> = ({ order, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<'details' | 'message'>('details');
  const [message, setMessage] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const { formatPrice } = useCurrency();

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this delivery?')) {
      return;
    }

    setLoading(true);
    try {
      await adminAPI.approveDelivery(order.id, notes || undefined);
      onUpdate();
      onClose();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to approve delivery');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    setLoading(true);
    try {
      await adminAPI.sendDispatcherMessage(order.id, message);
      alert('Message sent to dispatcher successfully');
      setMessage('');
      onUpdate();
      onClose();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const getApiBaseUrl = () => {
    return window.location.origin.replace(':5000', ':8000');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Delivery Review</h2>
            <p className="text-sm text-gray-600">Order #{order.id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-4 font-semibold border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-[#009FE3] text-[#009FE3]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Delivery Details
            </button>
            <button
              onClick={() => setActiveTab('message')}
              className={`py-3 px-4 font-semibold border-b-2 transition-colors ${
                activeTab === 'message'
                  ? 'border-[#009FE3] text-[#009FE3]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Send Message
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              {/* Order Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-900 mb-3">Order Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Customer</p>
                    <p className="font-semibold">{order.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Dispatcher</p>
                    <p className="font-semibold">{order.dispatcher_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Vehicle Type</p>
                    <p className="font-semibold capitalize">{order.vehicle_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Amount</p>
                    <p className="font-semibold">{formatPrice(order.final_price)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Package Type</p>
                    <p className="font-semibold">{order.package_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Completed At</p>
                    <p className="font-semibold">
                      {order.completed_at ? new Date(order.completed_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Route Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-900 mb-3">Delivery Route</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">Pickup Location</p>
                    <p className="font-semibold">{order.pickup_location}</p>
                    {order.stops && order.stops[0] && (
                      <div className="mt-1 text-xs text-gray-600">
                        <p>Contact: {order.stops[0].contact_name}</p>
                        <p>Phone: {order.stops[0].contact_phone}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Delivery Location</p>
                    <p className="font-semibold">{order.dropoff_location}</p>
                    {order.stops && order.stops[1] && (
                      <div className="mt-1 text-xs text-gray-600">
                        <p>Contact: {order.stops[1].contact_name}</p>
                        <p>Phone: {order.stops[1].contact_phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Delivery Photos</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Pickup Photo</p>
                    {order.pickup_photo_url ? (
                      <img
                        src={`${getApiBaseUrl()}${order.pickup_photo_url}`}
                        alt="Pickup"
                        className="w-full h-64 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400">No photo available</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Delivery Photo</p>
                    {order.delivery_photo_url ? (
                      <img
                        src={`${getApiBaseUrl()}${order.delivery_photo_url}`}
                        alt="Delivery"
                        className="w-full h-64 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400">No photo available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                  placeholder="Add any notes about this delivery..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Approve Delivery'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Sending a message will mark this delivery as "Needs Revision" 
                  and notify the dispatcher. Use this to request additional information or corrections.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message to Dispatcher
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                  placeholder="Describe what needs to be addressed..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  className="px-6 py-2 bg-[#F15A29] text-white rounded-lg hover:bg-[#d94f22] transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryReviewModal;
